/**
 * Currency Service - Geolocation & Currency Conversion
 * 
 * Provides:
 * - IP-based country detection
 * - Real-time exchange rates from USD
 * - Currency formatting
 * - Caching to minimize API calls
 */

// Base pricing in USD
export const BASE_PRICES_USD = {
  starter: 8,
  professional: 15,
  enterprise: null, // Custom pricing
};

// Complete Stripe-supported currencies (135+ currencies)
// All currencies Stripe accepts for presentment
export const CURRENCY_CONFIG = {
  // Major currencies
  USD: { symbol: '$', code: 'USD', locale: 'en-US', position: 'before' },
  EUR: { symbol: '€', code: 'EUR', locale: 'de-DE', position: 'before' },
  GBP: { symbol: '£', code: 'GBP', locale: 'en-GB', position: 'before' },
  JPY: { symbol: '¥', code: 'JPY', locale: 'ja-JP', position: 'before', decimals: 0 },
  CNY: { symbol: '¥', code: 'CNY', locale: 'zh-CN', position: 'before' },

  // Americas
  CAD: { symbol: 'C$', code: 'CAD', locale: 'en-CA', position: 'before' },
  MXN: { symbol: 'MX$', code: 'MXN', locale: 'es-MX', position: 'before' },
  BRL: { symbol: 'R$', code: 'BRL', locale: 'pt-BR', position: 'before' },
  ARS: { symbol: 'AR$', code: 'ARS', locale: 'es-AR', position: 'before' },
  CLP: { symbol: 'CL$', code: 'CLP', locale: 'es-CL', position: 'before', decimals: 0 },
  COP: { symbol: 'CO$', code: 'COP', locale: 'es-CO', position: 'before', decimals: 0 },
  PEN: { symbol: 'S/', code: 'PEN', locale: 'es-PE', position: 'before' },
  UYU: { symbol: '$U', code: 'UYU', locale: 'es-UY', position: 'before' },
  BOB: { symbol: 'Bs', code: 'BOB', locale: 'es-BO', position: 'before' },
  PYG: { symbol: '₲', code: 'PYG', locale: 'es-PY', position: 'before', decimals: 0 },
  VES: { symbol: 'Bs', code: 'VES', locale: 'es-VE', position: 'before' },
  CRC: { symbol: '₡', code: 'CRC', locale: 'es-CR', position: 'before' },
  GTQ: { symbol: 'Q', code: 'GTQ', locale: 'es-GT', position: 'before' },
  HNL: { symbol: 'L', code: 'HNL', locale: 'es-HN', position: 'before' },
  NIO: { symbol: 'C$', code: 'NIO', locale: 'es-NI', position: 'before' },
  PAB: { symbol: 'B/', code: 'PAB', locale: 'es-PA', position: 'before' },
  DOP: { symbol: 'RD$', code: 'DOP', locale: 'es-DO', position: 'before' },
  JMD: { symbol: 'J$', code: 'JMD', locale: 'en-JM', position: 'before' },
  TTD: { symbol: 'TT$', code: 'TTD', locale: 'en-TT', position: 'before' },
  BBD: { symbol: 'Bds$', code: 'BBD', locale: 'en-BB', position: 'before' },
  BSD: { symbol: 'B$', code: 'BSD', locale: 'en-BS', position: 'before' },
  BZD: { symbol: 'BZ$', code: 'BZD', locale: 'en-BZ', position: 'before' },
  GYD: { symbol: 'G$', code: 'GYD', locale: 'en-GY', position: 'before' },
  SRD: { symbol: 'Sr$', code: 'SRD', locale: 'nl-SR', position: 'before' },
  AWG: { symbol: 'Afl', code: 'AWG', locale: 'nl-AW', position: 'before' },
  ANG: { symbol: 'NAƒ', code: 'ANG', locale: 'nl-CW', position: 'before' },
  XCD: { symbol: 'EC$', code: 'XCD', locale: 'en-AG', position: 'before' },

  // Europe
  CHF: { symbol: 'CHF', code: 'CHF', locale: 'de-CH', position: 'before' },
  SEK: { symbol: 'kr', code: 'SEK', locale: 'sv-SE', position: 'after' },
  NOK: { symbol: 'kr', code: 'NOK', locale: 'nb-NO', position: 'after' },
  DKK: { symbol: 'kr', code: 'DKK', locale: 'da-DK', position: 'after' },
  PLN: { symbol: 'zł', code: 'PLN', locale: 'pl-PL', position: 'after' },
  CZK: { symbol: 'Kč', code: 'CZK', locale: 'cs-CZ', position: 'after' },
  HUF: { symbol: 'Ft', code: 'HUF', locale: 'hu-HU', position: 'after', decimals: 0 },
  RON: { symbol: 'lei', code: 'RON', locale: 'ro-RO', position: 'after' },
  BGN: { symbol: 'лв', code: 'BGN', locale: 'bg-BG', position: 'after' },
  HRK: { symbol: 'kn', code: 'HRK', locale: 'hr-HR', position: 'after' },
  RSD: { symbol: 'дин', code: 'RSD', locale: 'sr-RS', position: 'after' },
  ISK: { symbol: 'kr', code: 'ISK', locale: 'is-IS', position: 'after', decimals: 0 },
  RUB: { symbol: '₽', code: 'RUB', locale: 'ru-RU', position: 'after' },
  UAH: { symbol: '₴', code: 'UAH', locale: 'uk-UA', position: 'after' },
  GEL: { symbol: '₾', code: 'GEL', locale: 'ka-GE', position: 'after' },
  AMD: { symbol: '֏', code: 'AMD', locale: 'hy-AM', position: 'after' },
  AZN: { symbol: '₼', code: 'AZN', locale: 'az-AZ', position: 'after' },
  BYN: { symbol: 'Br', code: 'BYN', locale: 'be-BY', position: 'after' },
  MDL: { symbol: 'L', code: 'MDL', locale: 'ro-MD', position: 'after' },
  MKD: { symbol: 'ден', code: 'MKD', locale: 'mk-MK', position: 'after' },
  ALL: { symbol: 'L', code: 'ALL', locale: 'sq-AL', position: 'after' },
  BAM: { symbol: 'KM', code: 'BAM', locale: 'bs-BA', position: 'after' },

  // Asia Pacific
  AUD: { symbol: 'A$', code: 'AUD', locale: 'en-AU', position: 'before' },
  NZD: { symbol: 'NZ$', code: 'NZD', locale: 'en-NZ', position: 'before' },
  INR: { symbol: '₹', code: 'INR', locale: 'en-IN', position: 'before' },
  SGD: { symbol: 'S$', code: 'SGD', locale: 'en-SG', position: 'before' },
  HKD: { symbol: 'HK$', code: 'HKD', locale: 'zh-HK', position: 'before' },
  TWD: { symbol: 'NT$', code: 'TWD', locale: 'zh-TW', position: 'before' },
  KRW: { symbol: '₩', code: 'KRW', locale: 'ko-KR', position: 'before', decimals: 0 },
  PHP: { symbol: '₱', code: 'PHP', locale: 'en-PH', position: 'before' },
  THB: { symbol: '฿', code: 'THB', locale: 'th-TH', position: 'before' },
  MYR: { symbol: 'RM', code: 'MYR', locale: 'ms-MY', position: 'before' },
  IDR: { symbol: 'Rp', code: 'IDR', locale: 'id-ID', position: 'before', decimals: 0 },
  VND: { symbol: '₫', code: 'VND', locale: 'vi-VN', position: 'after', decimals: 0 },
  PKR: { symbol: '₨', code: 'PKR', locale: 'en-PK', position: 'before' },
  BDT: { symbol: '৳', code: 'BDT', locale: 'bn-BD', position: 'before' },
  LKR: { symbol: 'Rs', code: 'LKR', locale: 'si-LK', position: 'before' },
  NPR: { symbol: 'रू', code: 'NPR', locale: 'ne-NP', position: 'before' },
  MMK: { symbol: 'K', code: 'MMK', locale: 'my-MM', position: 'before' },
  KHR: { symbol: '៛', code: 'KHR', locale: 'km-KH', position: 'before' },
  LAK: { symbol: '₭', code: 'LAK', locale: 'lo-LA', position: 'before', decimals: 0 },
  BND: { symbol: 'B$', code: 'BND', locale: 'ms-BN', position: 'before' },
  MNT: { symbol: '₮', code: 'MNT', locale: 'mn-MN', position: 'before' },
  KZT: { symbol: '₸', code: 'KZT', locale: 'kk-KZ', position: 'before' },
  UZS: { symbol: 'сўм', code: 'UZS', locale: 'uz-UZ', position: 'after', decimals: 0 },
  KGS: { symbol: 'сом', code: 'KGS', locale: 'ky-KG', position: 'after' },
  TJS: { symbol: 'ЅМ', code: 'TJS', locale: 'tg-TJ', position: 'after' },
  AFN: { symbol: '؋', code: 'AFN', locale: 'fa-AF', position: 'before' },
  FJD: { symbol: 'FJ$', code: 'FJD', locale: 'en-FJ', position: 'before' },
  PGK: { symbol: 'K', code: 'PGK', locale: 'en-PG', position: 'before' },
  WST: { symbol: 'WS$', code: 'WST', locale: 'en-WS', position: 'before' },
  SBD: { symbol: 'SI$', code: 'SBD', locale: 'en-SB', position: 'before' },
  TOP: { symbol: 'T$', code: 'TOP', locale: 'to-TO', position: 'before' },
  VUV: { symbol: 'VT', code: 'VUV', locale: 'en-VU', position: 'before', decimals: 0 },

  // Middle East
  AED: { symbol: 'AED', code: 'AED', locale: 'ar-AE', position: 'before' },
  SAR: { symbol: 'SAR', code: 'SAR', locale: 'ar-SA', position: 'before' },
  QAR: { symbol: 'QAR', code: 'QAR', locale: 'ar-QA', position: 'before' },
  KWD: { symbol: 'KD', code: 'KWD', locale: 'ar-KW', position: 'before', decimals: 3 },
  BHD: { symbol: 'BD', code: 'BHD', locale: 'ar-BH', position: 'before', decimals: 3 },
  OMR: { symbol: 'OMR', code: 'OMR', locale: 'ar-OM', position: 'before', decimals: 3 },
  JOD: { symbol: 'JD', code: 'JOD', locale: 'ar-JO', position: 'before', decimals: 3 },
  ILS: { symbol: '₪', code: 'ILS', locale: 'he-IL', position: 'before' },
  TRY: { symbol: '₺', code: 'TRY', locale: 'tr-TR', position: 'before' },
  LBP: { symbol: 'ل.ل', code: 'LBP', locale: 'ar-LB', position: 'before' },
  EGP: { symbol: 'E£', code: 'EGP', locale: 'ar-EG', position: 'before' },
  IQD: { symbol: 'ع.د', code: 'IQD', locale: 'ar-IQ', position: 'before', decimals: 0 },
  YER: { symbol: '﷼', code: 'YER', locale: 'ar-YE', position: 'before' },
  SYP: { symbol: '£S', code: 'SYP', locale: 'ar-SY', position: 'before' },

  // Africa
  ZAR: { symbol: 'R', code: 'ZAR', locale: 'en-ZA', position: 'before' },
  NGN: { symbol: '₦', code: 'NGN', locale: 'en-NG', position: 'before' },
  KES: { symbol: 'KSh', code: 'KES', locale: 'en-KE', position: 'before' },
  GHS: { symbol: 'GH₵', code: 'GHS', locale: 'en-GH', position: 'before' },
  UGX: { symbol: 'USh', code: 'UGX', locale: 'en-UG', position: 'before', decimals: 0 },
  TZS: { symbol: 'TSh', code: 'TZS', locale: 'sw-TZ', position: 'before' },
  RWF: { symbol: 'RF', code: 'RWF', locale: 'rw-RW', position: 'before', decimals: 0 },
  ETB: { symbol: 'Br', code: 'ETB', locale: 'am-ET', position: 'before' },
  MAD: { symbol: 'DH', code: 'MAD', locale: 'ar-MA', position: 'before' },
  DZD: { symbol: 'DA', code: 'DZD', locale: 'ar-DZ', position: 'before' },
  TND: { symbol: 'DT', code: 'TND', locale: 'ar-TN', position: 'before', decimals: 3 },
  LYD: { symbol: 'LD', code: 'LYD', locale: 'ar-LY', position: 'before', decimals: 3 },
  XOF: { symbol: 'CFA', code: 'XOF', locale: 'fr-SN', position: 'after', decimals: 0 },
  XAF: { symbol: 'FCFA', code: 'XAF', locale: 'fr-CM', position: 'after', decimals: 0 },
  MUR: { symbol: '₨', code: 'MUR', locale: 'en-MU', position: 'before' },
  SCR: { symbol: '₨', code: 'SCR', locale: 'en-SC', position: 'before' },
  BWP: { symbol: 'P', code: 'BWP', locale: 'en-BW', position: 'before' },
  NAD: { symbol: 'N$', code: 'NAD', locale: 'en-NA', position: 'before' },
  SZL: { symbol: 'E', code: 'SZL', locale: 'en-SZ', position: 'before' },
  LSL: { symbol: 'L', code: 'LSL', locale: 'en-LS', position: 'before' },
  MWK: { symbol: 'MK', code: 'MWK', locale: 'en-MW', position: 'before' },
  ZMW: { symbol: 'ZK', code: 'ZMW', locale: 'en-ZM', position: 'before' },
  MZN: { symbol: 'MT', code: 'MZN', locale: 'pt-MZ', position: 'before' },
  AOA: { symbol: 'Kz', code: 'AOA', locale: 'pt-AO', position: 'before' },
  CVE: { symbol: '$', code: 'CVE', locale: 'pt-CV', position: 'before' },
  GMD: { symbol: 'D', code: 'GMD', locale: 'en-GM', position: 'before' },
  GNF: { symbol: 'FG', code: 'GNF', locale: 'fr-GN', position: 'before', decimals: 0 },
  SLL: { symbol: 'Le', code: 'SLL', locale: 'en-SL', position: 'before' },
  LRD: { symbol: 'L$', code: 'LRD', locale: 'en-LR', position: 'before' },
  MGA: { symbol: 'Ar', code: 'MGA', locale: 'mg-MG', position: 'before' },
  CDF: { symbol: 'FC', code: 'CDF', locale: 'fr-CD', position: 'before' },
  BIF: { symbol: 'FBu', code: 'BIF', locale: 'fr-BI', position: 'before', decimals: 0 },
  DJF: { symbol: 'Fdj', code: 'DJF', locale: 'fr-DJ', position: 'before', decimals: 0 },
  ERN: { symbol: 'Nfk', code: 'ERN', locale: 'ti-ER', position: 'before' },
  SOS: { symbol: 'Sh', code: 'SOS', locale: 'so-SO', position: 'before' },
  SDG: { symbol: 'ج.س', code: 'SDG', locale: 'ar-SD', position: 'before' },
  SSP: { symbol: '£', code: 'SSP', locale: 'en-SS', position: 'before' },
  STN: { symbol: 'Db', code: 'STN', locale: 'pt-ST', position: 'before' },
  KMF: { symbol: 'CF', code: 'KMF', locale: 'fr-KM', position: 'before', decimals: 0 },
};

// Complete country to currency mapping (ISO 3166-1 alpha-2 to currency)
export const COUNTRY_CURRENCY_MAP = {
  // Americas
  US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
  UY: 'UYU', BO: 'BOB', PY: 'PYG', VE: 'VES', EC: 'USD', CR: 'CRC', GT: 'GTQ', HN: 'HNL',
  NI: 'NIO', PA: 'PAB', DO: 'DOP', JM: 'JMD', TT: 'TTD', BB: 'BBD', BS: 'BSD', BZ: 'BZD',
  GY: 'GYD', SR: 'SRD', AW: 'AWG', CW: 'ANG', SX: 'ANG', PR: 'USD', VI: 'USD',
  AG: 'XCD', DM: 'XCD', GD: 'XCD', KN: 'XCD', LC: 'XCD', VC: 'XCD', AI: 'XCD', MS: 'XCD',
  HT: 'USD', CU: 'USD',

  // Europe - Eurozone
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR',
  PT: 'EUR', FI: 'EUR', GR: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR',
  CY: 'EUR', MT: 'EUR', LU: 'EUR', MC: 'EUR', SM: 'EUR', VA: 'EUR', AD: 'EUR', XK: 'EUR',
  ME: 'EUR',

  // Europe - Non-Eurozone
  GB: 'GBP', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  RO: 'RON', BG: 'BGN', HR: 'EUR', RS: 'RSD', IS: 'ISK', RU: 'RUB', UA: 'UAH', BY: 'BYN',
  MD: 'MDL', MK: 'MKD', AL: 'ALL', BA: 'BAM', GE: 'GEL', AM: 'AMD', AZ: 'AZN', LI: 'CHF',

  // Asia Pacific
  AU: 'AUD', NZ: 'NZD', JP: 'JPY', CN: 'CNY', HK: 'HKD', TW: 'TWD', KR: 'KRW', SG: 'SGD',
  IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR', BT: 'INR', MV: 'USD',
  PH: 'PHP', TH: 'THB', MY: 'MYR', ID: 'IDR', VN: 'VND', MM: 'MMK', KH: 'KHR', LA: 'LAK',
  BN: 'BND', TL: 'USD', MN: 'MNT', KZ: 'KZT', UZ: 'UZS', KG: 'KGS', TJ: 'TJS', TM: 'USD',
  AF: 'AFN', FJ: 'FJD', PG: 'PGK', WS: 'WST', SB: 'SBD', TO: 'TOP', VU: 'VUV', NC: 'XPF',
  PF: 'XPF', GU: 'USD', MP: 'USD', AS: 'USD', FM: 'USD', MH: 'USD', PW: 'USD',

  // Middle East
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', JO: 'JOD', IL: 'ILS',
  TR: 'TRY', LB: 'LBP', EG: 'EGP', IQ: 'IQD', IR: 'USD', YE: 'YER', SY: 'SYP', PS: 'ILS',

  // Africa
  ZA: 'ZAR', NG: 'NGN', KE: 'KES', GH: 'GHS', UG: 'UGX', TZ: 'TZS', RW: 'RWF', ET: 'ETB',
  MA: 'MAD', DZ: 'DZD', TN: 'TND', LY: 'LYD', EH: 'MAD',
  SN: 'XOF', CI: 'XOF', ML: 'XOF', BF: 'XOF', NE: 'XOF', TG: 'XOF', BJ: 'XOF', GW: 'XOF',
  CM: 'XAF', GA: 'XAF', CG: 'XAF', TD: 'XAF', CF: 'XAF', GQ: 'XAF',
  MU: 'MUR', SC: 'SCR', BW: 'BWP', NA: 'NAD', SZ: 'SZL', LS: 'LSL', MW: 'MWK', ZM: 'ZMW',
  ZW: 'USD', MZ: 'MZN', AO: 'AOA', CV: 'CVE', GM: 'GMD', GN: 'GNF', SL: 'SLL', LR: 'LRD',
  MG: 'MGA', CD: 'CDF', BI: 'BIF', DJ: 'DJF', ER: 'ERN', SO: 'SOS', SD: 'SDG', SS: 'SSP',
  ST: 'STN', KM: 'KMF', RE: 'EUR', YT: 'EUR', MR: 'USD',
};

// Cache for exchange rates (1 hour TTL)
let ratesCache = { rates: null, timestamp: 0 };
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// Cache for country detection
let countryCache = { country: null, currency: null };

/**
 * Detect visitor's country using IP geolocation
 * Uses multiple fallback APIs for reliability
 */
export const detectCountry = async () => {
  if (countryCache.country) {
    return countryCache;
  }

  const apis = [
    { url: 'https://ipapi.co/json/', parser: (d) => d.country_code },
    { url: 'https://ip-api.com/json/?fields=countryCode', parser: (d) => d.countryCode },
    { url: 'https://ipinfo.io/json', parser: (d) => d.country },
  ];

  for (const api of apis) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch(api.url, { signal: controller.signal });
      clearTimeout(timeout);
      
      if (response.ok) {
        const data = await response.json();
        const countryCode = api.parser(data);
        const currency = COUNTRY_CURRENCY_MAP[countryCode] || 'USD';
        
        countryCache = { country: countryCode, currency };
        return countryCache;
      }
    } catch (error) {
      console.warn(`Geolocation API failed:`, error.message);
      continue;
    }
  }

  // Fallback to USD
  countryCache = { country: 'US', currency: 'USD' };
  return countryCache;
};

// Fallback rates (updated January 2026 - update periodically)
// These are approximate rates - live rates are fetched from API
// Current rate: 1 USD = 91 INR (as of Jan 31, 2026)
const FALLBACK_RATES = {
  // Major currencies
  USD: 1, EUR: 0.92, GBP: 0.79, JPY: 155, CNY: 7.25,

  // Americas
  CAD: 1.44, MXN: 20.50, BRL: 5.85, ARS: 1050, CLP: 980, COP: 4200, PEN: 3.75,
  UYU: 44, BOB: 6.91, PYG: 7800, VES: 40, CRC: 510, GTQ: 7.75, HNL: 25,
  NIO: 37, PAB: 1, DOP: 60, JMD: 156, TTD: 6.80, BBD: 2, BSD: 1, BZD: 2,
  GYD: 210, SRD: 32, AWG: 1.79, ANG: 1.79, XCD: 2.70,

  // Europe
  CHF: 0.91, SEK: 10.95, NOK: 11.20, DKK: 7.05, PLN: 4.05, CZK: 23.50,
  HUF: 380, RON: 4.70, BGN: 1.85, HRK: 7.10, RSD: 110, ISK: 140,
  RUB: 92, UAH: 42, GEL: 2.75, AMD: 400, AZN: 1.70, BYN: 3.30,
  MDL: 18, MKD: 58, ALL: 95, BAM: 1.85,

  // Asia Pacific
  AUD: 1.57, NZD: 1.77, INR: 91, SGD: 1.35, HKD: 7.80, TWD: 32, KRW: 1350,
  PHP: 57, THB: 34, MYR: 4.45, IDR: 16200, VND: 25400, PKR: 280, BDT: 122,
  LKR: 320, NPR: 135, MMK: 2100, KHR: 4100, LAK: 21000, BND: 1.35, MNT: 3450,
  KZT: 510, UZS: 12800, KGS: 90, TJS: 11, AFN: 70, FJD: 2.30, PGK: 3.95,
  WST: 2.80, SBD: 8.50, TOP: 2.40, VUV: 120,

  // Middle East
  AED: 3.67, SAR: 3.75, QAR: 3.64, KWD: 0.31, BHD: 0.38, OMR: 0.38, JOD: 0.71,
  ILS: 3.65, TRY: 32, LBP: 89500, EGP: 50, IQD: 1310, YER: 250, SYP: 13000,

  // Africa
  ZAR: 18.35, NGN: 1600, KES: 155, GHS: 16, UGX: 3750, TZS: 2650, RWF: 1350,
  ETB: 125, MAD: 10, DZD: 135, TND: 3.15, LYD: 4.90, XOF: 620, XAF: 620,
  MUR: 46, SCR: 14, BWP: 14, NAD: 18.35, SZL: 18.35, LSL: 18.35, MWK: 1750,
  ZMW: 28, MZN: 64, AOA: 830, CVE: 104, GMD: 72, GNF: 8600, SLL: 22500,
  LRD: 195, MGA: 4650, CDF: 2800, BIF: 2900, DJF: 178, ERN: 15, SOS: 570,
  SDG: 600, SSP: 1300, STN: 23, KMF: 465,
};

/**
 * Fetch exchange rates from USD to other currencies
 * Uses multiple APIs with fallback
 */
export const fetchExchangeRates = async () => {
  const now = Date.now();

  if (ratesCache.rates && (now - ratesCache.timestamp) < CACHE_TTL) {
    return ratesCache.rates;
  }

  // List of free exchange rate APIs to try
  const apis = [
    {
      url: 'https://open.er-api.com/v6/latest/USD',
      parser: (data) => data.rates
    },
    {
      url: 'https://api.exchangerate.host/latest?base=USD',
      parser: (data) => data.rates
    },
    {
      url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
      parser: (data) => {
        // This API returns lowercase keys, need to transform
        const rates = {};
        Object.keys(data.usd || {}).forEach(key => {
          rates[key.toUpperCase()] = data.usd[key];
        });
        return rates;
      }
    }
  ];

  for (const api of apis) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(api.url, { signal: controller.signal });
      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        const rates = api.parser(data);

        // Validate that we got actual rates
        if (rates && typeof rates === 'object' && rates.INR && rates.EUR) {
          console.log('Exchange rates loaded from API');
          ratesCache = { rates, timestamp: now };
          return rates;
        }
      }
    } catch (error) {
      console.warn(`Exchange rate API failed:`, error.message);
      continue;
    }
  }

  // Use fallback rates if all APIs fail
  console.log('Using fallback exchange rates');
  ratesCache = { rates: FALLBACK_RATES, timestamp: now };
  return FALLBACK_RATES;
};

/**
 * Convert USD amount to target currency
 */
export const convertPrice = (usdAmount, rates, targetCurrency) => {
  if (!usdAmount || usdAmount === null) return null;
  // Safety check: ensure rates object exists
  if (!rates || typeof rates !== 'object') {
    return Math.round(usdAmount); // Return USD amount if rates not available
  }
  const rate = rates[targetCurrency] || 1;
  return Math.round(usdAmount * rate);
};

/**
 * Format price with currency symbol
 */
export const formatPrice = (amount, currencyCode = 'USD') => {
  if (amount === null || amount === undefined) return 'Custom';
  
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.USD;
  const decimals = config.decimals !== undefined ? config.decimals : (amount < 100 ? 0 : 0);
  const formatted = amount.toLocaleString(config.locale, { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
  
  return config.position === 'after' 
    ? `${formatted} ${config.symbol}` 
    : `${config.symbol}${formatted}`;
};

/**
 * Get annual price with 25% discount
 */
export const getAnnualPrice = (monthlyPrice) => {
  if (!monthlyPrice) return null;
  return Math.round(monthlyPrice * 12 * 0.75); // 25% annual discount
};

